import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import api from '../services/api';
import './ClientDashboard.css';
import './CreditsPage.css';

const CreditsPage = () => {
  const [activeTab, setActiveTab] = useState('tokens');
  const [balance, setBalance] = useState({ availableCredits: 0, totalCreditsPurchased: 0, totalCreditsUsed: 0 });
  const [packages, setPackages] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [userAvencas, setUserAvencas] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Card');
  // Token state
  const [tokenBalance, setTokenBalance] = useState({ balance: 0, totalEarned: 0, totalSpent: 0 });
  const [tokenShopItems, setTokenShopItems] = useState([]);
  const [tokenTransactions, setTokenTransactions] = useState([]);
  const [tokenPurchases, setTokenPurchases] = useState([]);
  const [aiCosts, setAiCosts] = useState([]);
  const [purchasingItem, setPurchasingItem] = useState(null);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/balance', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      }
    } catch (error) {
      console.error('Erro ao carregar saldo:', error);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/packages');
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    }
  }, []);

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/subscriptions/plans');
      if (response.ok) {
        const data = await response.json();
        setSubscriptionPlans(data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  }, []);

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/subscriptions/current', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.hasSubscription) {
          setCurrentSubscription(data.subscription);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar subscrição:', error);
    }
  }, []);

  const fetchMyAvencas = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/my/avencas', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserAvencas(data);
      }
    } catch (error) {
      console.error('Erro ao carregar avenças:', error);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await fetch('/api/credits/transactions', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
    }
  }, []);

  // Token fetches
  const fetchTokenBalance = useCallback(async () => {
    try {
      const res = await api.get('/TokenShop/balance');
      setTokenBalance(res.data);
    } catch (e) { console.error('Erro tokens:', e); }
  }, []);

  const fetchTokenShopItems = useCallback(async () => {
    try {
      const res = await api.get('/TokenShop/items');
      setTokenShopItems(res.data);
    } catch (e) { console.error('Erro loja tokens:', e); }
  }, []);

  const fetchTokenTransactions = useCallback(async () => {
    try {
      const res = await api.get('/TokenShop/my-transactions');
      setTokenTransactions(res.data);
    } catch (e) { console.error('Erro transações tokens:', e); }
  }, []);

  const fetchTokenPurchases = useCallback(async () => {
    try {
      const res = await api.get('/TokenShop/my-purchases');
      setTokenPurchases(res.data);
    } catch (e) { console.error('Erro compras tokens:', e); }
  }, []);

  const fetchAiCosts = useCallback(async () => {
    try {
      const res = await api.get('/TokenShop/ai-costs');
      setAiCosts(res.data);
    } catch (e) { console.error('Erro custos IA:', e); }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBalance(),
        fetchPackages(),
        fetchSubscriptionPlans(),
        fetchCurrentSubscription(),
        fetchMyAvencas(),
        fetchTransactions(),
        fetchTokenBalance(),
        fetchTokenShopItems(),
        fetchTokenTransactions(),
        fetchTokenPurchases(),
        fetchAiCosts()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchBalance, fetchPackages, fetchSubscriptionPlans, fetchCurrentSubscription, fetchMyAvencas, fetchTransactions, fetchTokenBalance, fetchTokenShopItems, fetchTokenTransactions, fetchTokenPurchases, fetchAiCosts]);

  const openPurchaseModal = (pkg) => {
    setSelectedPackage(pkg);
    setShowPurchaseModal(true);
  };

  const purchasePackage = async () => {
    try {
      const response = await fetch(`/api/credits/purchase/${selectedPackage.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentMethod })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.message} +${data.creditsAdded} créditos!`);
        setShowPurchaseModal(false);
        fetchBalance();
        fetchTransactions();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erro na compra');
      }
    } catch (error) {
      toast.error('Erro ao processar compra');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d 'de' MMMM, HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'Purchase': return '💳';
      case 'Use': return '🏋️';
      case 'Bonus': return '🎁';
      case 'Expired': return '⏰';
      default: return '📝';
    }
  };

  const handleTokenPurchase = async (item) => {
    if (tokenBalance.balance < item.price) {
      toast.error(`Saldo insuficiente! Precisa de ${item.price} tokens, tem ${tokenBalance.balance}.`);
      return;
    }
    if (!window.confirm(`Comprar "${item.name}" por ${item.price} tokens?`)) return;
    setPurchasingItem(item.id);
    try {
      const res = await api.post(`/TokenShop/purchase/${item.id}`);
      toast.success(res.data.message);
      fetchTokenBalance();
      fetchTokenPurchases();
      fetchTokenTransactions();
    } catch (error) {
      toast.error(error.response?.data || 'Erro ao comprar item');
    } finally {
      setPurchasingItem(null);
    }
  };

  const getTokenTxIcon = (type) => {
    switch (type) {
      case 'ai_usage': return '🤖';
      case 'spent': return '🛍️';
      case 'admin_distribute': return '🎁';
      case 'Plano Aprovado': case 'Plano Alimentar Aprovado': return '✅';
      case 'bonus_inicial': return '🎉';
      default: return '🪙';
    }
  };

  // Group token shop items by category
  const groupedShopItems = tokenShopItems.reduce((acc, item) => {
    const cat = item.category || 'Geral';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const renderTokens = () => (
    <div>
      {/* Token Wallet */}
      <div className="token-wallet-card">
        <div className="token-wallet-main">
          <span className="token-wallet-icon">🪙</span>
          <div className="token-wallet-balance">{tokenBalance.balance}</div>
          <div className="token-wallet-label">tokens disponíveis</div>
        </div>
        <div className="token-wallet-stats">
          <div className="token-wallet-stat">
            <span className="token-wallet-stat-value">{tokenBalance.totalEarned}</span>
            <span className="token-wallet-stat-label">📈 Ganhos</span>
          </div>
          <div className="token-wallet-stat">
            <span className="token-wallet-stat-value">{tokenBalance.totalSpent}</span>
            <span className="token-wallet-stat-label">📉 Gastos</span>
          </div>
        </div>
      </div>

      {/* AI Costs Info */}
      {aiCosts.length > 0 && (
        <div className="ai-costs-info">
          <h4 className="section-title">🤖 Custo de Tokens por Funcionalidade IA</h4>
          <div className="ai-costs-grid">
            {aiCosts.map(c => (
              <div key={c.id} className="ai-cost-item">
                <span className="ai-cost-label">{c.featureLabel}</span>
                <span className="ai-cost-value">{c.tokenCost} 🪙</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Token Shop */}
      {Object.keys(groupedShopItems).length > 0 && (
        <>
          <h4 className="section-title" style={{ marginTop: '24px' }}>🛍️ Loja de Tokens</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '16px' }}>
            Troque os seus tokens por recompensas e benefícios exclusivos!
          </p>
          {Object.entries(groupedShopItems).map(([category, items]) => (
            <div key={category} style={{ marginBottom: '20px' }}>
              <h5 style={{ color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>{category}</h5>
              <div className="token-shop-grid">
                {items.map(item => (
                  <div key={item.id} className="token-shop-item">
                    <div className="token-shop-item-icon">{item.icon || '🎁'}</div>
                    <div className="token-shop-item-info">
                      <div className="token-shop-item-name">{item.name}</div>
                      <div className="token-shop-item-desc">{item.description}</div>
                    </div>
                    <div className="token-shop-item-action">
                      <div className="token-shop-item-price">{item.price} 🪙</div>
                      <button
                        className="token-shop-buy-btn"
                        disabled={purchasingItem === item.id || tokenBalance.balance < item.price}
                        onClick={() => handleTokenPurchase(item)}
                      >
                        {purchasingItem === item.id ? '...' : 'Trocar'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Token Transaction History */}
      <h4 className="section-title" style={{ marginTop: '24px' }}>📊 Histórico de Tokens</h4>
      <div className="transactions-list">
        {tokenTransactions.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🪙</div>
            <p style={{ color: 'var(--text-secondary)' }}>Sem transações de tokens ainda</p>
          </div>
        ) : (
          tokenTransactions.slice(0, 20).map(tx => (
            <div key={tx.id} className="transaction-item">
              <div className={`transaction-icon ${tx.amount > 0 ? 'positive' : 'negative'}`} style={{ fontSize: '20px' }}>
                {getTokenTxIcon(tx.type)}
              </div>
              <div className="transaction-details">
                <div className="transaction-description">{tx.description}</div>
                <div className="transaction-date">{formatDate(tx.createdAt)}</div>
              </div>
              <div className={`transaction-amount ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                {tx.amount > 0 ? '+' : ''}{tx.amount} 🪙
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pending Purchases */}
      {tokenPurchases.filter(p => p.status === 'pending').length > 0 && (
        <>
          <h4 className="section-title" style={{ marginTop: '24px' }}>⏳ Compras Pendentes</h4>
          <div className="transactions-list">
            {tokenPurchases.filter(p => p.status === 'pending').map(p => (
              <div key={p.id} className="transaction-item">
                <div className="transaction-icon" style={{ fontSize: '20px' }}>{p.itemIcon || '🛍️'}</div>
                <div className="transaction-details">
                  <div className="transaction-description">{p.itemName}</div>
                  <div className="transaction-date">A aguardar aprovação do admin</div>
                </div>
                <div className="transaction-amount negative">-{p.tokensSpent} 🪙</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderPackages = () => (
    <div>
      <h4 className="section-title">💳 Pacotes de Créditos</h4>
      <div className="packages-grid">
        {packages.map(pkg => (
          <div 
            key={pkg.id} 
            className={`package-card ${pkg.isPopular ? 'popular' : ''}`}
            onClick={() => openPurchaseModal(pkg)}
          >
            {pkg.isPopular && <span className="package-popular-badge">⭐ Mais Popular</span>}
            <div className="package-icon">{pkg.icon || '💳'}</div>
            <div className="package-name">{pkg.name}</div>
            <div className="package-credits">
              {pkg.credits} <span>créditos</span>
            </div>
            <p className="package-description">{pkg.description}</p>
            <div className="package-price">
              €{pkg.price?.toFixed(2)} <span>EUR</span>
            </div>
            {pkg.validityDays && (
              <div className="package-validity">Válido por {pkg.validityDays} dias</div>
            )}
            <button className="package-buy-btn">
              Comprar Agora
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const getPeriodicityLabel = (p) => {
    switch (p) {
      case 'Monthly': return 'Mensal';
      case 'Quarterly': return 'Trimestral';
      case 'SemiAnnual': return 'Semestral';
      case 'Annual': return 'Anual';
      default: return p;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return { label: 'Ativa', className: 'avenca-status-active' };
      case 'Paused': return { label: 'Pausada', className: 'avenca-status-paused' };
      case 'Cancelled': return { label: 'Cancelada', className: 'avenca-status-cancelled' };
      case 'Expired': return { label: 'Expirada', className: 'avenca-status-expired' };
      default: return { label: status, className: '' };
    }
  };

  const renderSubscriptions = () => (
    <div>
      {/* Avenças do Cliente */}
      {userAvencas.length > 0 && (
        <>
          <h4 className="section-title">🔄 As Minhas Avenças</h4>
          <div className="avencas-grid">
            {userAvencas.map(av => {
              const statusInfo = getStatusBadge(av.status);
              return (
                <div key={av.id} className={`avenca-card ${av.status === 'Active' ? 'avenca-active' : ''}`} style={{ borderLeftColor: av.color || '#3498db' }}>
                  <div className="avenca-card-header">
                    <span className="avenca-name">{av.tipoName}</span>
                    <span className={`avenca-status-badge ${statusInfo.className}`}>{statusInfo.label}</span>
                  </div>
                  {av.tipoDescription && <p className="avenca-description">{av.tipoDescription}</p>}
                  <div className="avenca-amount">€{av.amount?.toFixed(2)} <span>/ {getPeriodicityLabel(av.periodicity)}</span></div>
                  <div className="avenca-details">
                    <div className="avenca-detail-row">
                      <span>📅 Início:</span>
                      <span>{formatDate(av.startDate)}</span>
                    </div>
                    {av.endDate && (
                      <div className="avenca-detail-row">
                        <span>🏁 Fim:</span>
                        <span>{formatDate(av.endDate)}</span>
                      </div>
                    )}
                    {av.nextGenerationDate && av.status === 'Active' && (
                      <div className="avenca-detail-row">
                        <span>📄 Próxima fatura:</span>
                        <span>{formatDate(av.nextGenerationDate)}</span>
                      </div>
                    )}
                  </div>
                  {av.notes && <div className="avenca-notes">💬 {av.notes}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}

      <h4 className="section-title" style={{ marginTop: userAvencas.length > 0 ? '32px' : '0' }}>📋 Planos de Subscrição</h4>
      <div className="subscription-plans">
        {subscriptionPlans.map(plan => (
          <div 
            key={plan.id} 
            className={`subscription-card ${currentSubscription?.plan?.id === plan.id ? 'active' : ''}`}
          >
            {currentSubscription?.plan?.id === plan.id && (
              <span className="subscription-current-badge">✓ Seu Plano Atual</span>
            )}
            <div className="subscription-name">{plan.name}</div>
            <div className="subscription-price">
              €{plan.monthlyPrice?.toFixed(2)} <span>/mês</span>
            </div>
            <p className="subscription-description">{plan.description}</p>
            <ul className="subscription-features">
              {plan.monthlyCredits > 0 && (
                <li>{plan.monthlyCredits} créditos por mês</li>
              )}
              {plan.unlimitedBookings && <li>Reservas ilimitadas</li>}
              {plan.includesOnlineClasses && <li>Aulas online incluídas</li>}
              {plan.priorityBooking && <li>Reserva prioritária</li>}
            </ul>
            <button 
              className={`subscription-btn ${currentSubscription?.plan?.id === plan.id ? 'outline' : ''}`}
              disabled={currentSubscription?.plan?.id === plan.id}
            >
              {currentSubscription?.plan?.id === plan.id ? 'Plano Atual' : 'Escolher Plano'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div>
      <h4 className="section-title">📊 Histórico de Transações</h4>
      <div className="transactions-list">
        {transactions.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Sem transações</h3>
            <p style={{ color: 'var(--text-secondary)' }}>As suas transações aparecerão aqui</p>
          </div>
        ) : (
          transactions.map(tx => (
            <div key={tx.id} className="transaction-item">
              <div className={`transaction-icon ${tx.type.toLowerCase()}`}>
                {getTransactionIcon(tx.type)}
              </div>
              <div className="transaction-details">
                <div className="transaction-description">{tx.description}</div>
                <div className="transaction-date">{formatDate(tx.createdAt)}</div>
              </div>
              <div>
                <div className={`transaction-amount ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                  {tx.amount > 0 ? '+' : ''}{tx.amount}
                </div>
                <div className="transaction-balance">Saldo: {tx.balanceAfter}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="data-card">
          <div className="empty-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <h3 style={{ color: 'var(--text-primary)' }}>A carregar...</h3>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="credits-page">
        {/* Dual Balance Cards */}
        <div className="dual-balance-row">
          <div className="balance-card token-balance-card">
            <div className="balance-label">🪙 Tokens</div>
            <div className="balance-amount">
              {tokenBalance.balance}
              <span className="balance-currency">tokens</span>
            </div>
            <div className="balance-stats">
              <div className="balance-stat">
                <div className="balance-stat-value">{tokenBalance.totalEarned}</div>
                <div className="balance-stat-label">Ganhos</div>
              </div>
              <div className="balance-stat">
                <div className="balance-stat-value">{tokenBalance.totalSpent}</div>
                <div className="balance-stat-label">Gastos</div>
              </div>
            </div>
          </div>
          <div className="balance-card credit-balance-card">
            <div className="balance-label">💳 Créditos</div>
            <div className="balance-amount">
              {balance.availableCredits}
              <span className="balance-currency">créditos</span>
            </div>
            <div className="balance-stats">
              <div className="balance-stat">
                <div className="balance-stat-value">{balance.totalCreditsPurchased}</div>
                <div className="balance-stat-label">Comprados</div>
              </div>
              <div className="balance-stat">
                <div className="balance-stat-value">{balance.totalCreditsUsed}</div>
                <div className="balance-stat-label">Usados</div>
              </div>
            </div>
            {balance.creditsExpireAt && (
              <div className="balance-expiry">
                ⏰ Expira em: {formatDate(balance.creditsExpireAt)}
              </div>
            )}
          </div>
        </div>

        <div className="data-card">
          <div className="card-header">
            <h3 className="card-title">💳 Créditos & Tokens</h3>
          </div>

          {/* Tabs */}
          <div className="credits-tabs">
            <button 
              className={`credits-tab ${activeTab === 'tokens' ? 'active' : ''}`}
              onClick={() => setActiveTab('tokens')}
            >
              🪙 Tokens
            </button>
            <button 
              className={`credits-tab ${activeTab === 'packages' ? 'active' : ''}`}
              onClick={() => setActiveTab('packages')}
            >
              💳 Pacotes
            </button>
            <button 
              className={`credits-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscriptions')}
            >
              📋 Subscrições
            </button>
            <button 
              className={`credits-tab ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              📊 Histórico
            </button>
          </div>

          {activeTab === 'tokens' && renderTokens()}
          {activeTab === 'packages' && renderPackages()}
          {activeTab === 'subscriptions' && renderSubscriptions()}
          {activeTab === 'transactions' && renderTransactions()}
        </div>
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedPackage && (
        <div className="purchase-modal" onClick={() => setShowPurchaseModal(false)}>
          <div className="purchase-modal-content" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '16px', color: 'var(--text-primary)' }}>
              💳 Comprar {selectedPackage.name}
            </h3>
            
            <div className="purchase-summary">
              <div className="purchase-row">
                <span>Pacote:</span>
                <span>{selectedPackage.name}</span>
              </div>
              <div className="purchase-row">
                <span>Créditos:</span>
                <span>{selectedPackage.credits}</span>
              </div>
              <div className="purchase-row">
                <span>Total:</span>
                <span style={{ fontWeight: 700, color: 'var(--accent-primary)' }}>€{selectedPackage.price?.toFixed(2)}</span>
              </div>
            </div>

            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Método de Pagamento:
            </p>
            <div className="payment-methods">
              <div 
                className={`payment-method ${paymentMethod === 'Card' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('Card')}
              >
                <div className="payment-method-icon">💳</div>
                <div className="payment-method-name">Cartão</div>
              </div>
              <div 
                className={`payment-method ${paymentMethod === 'MBWay' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('MBWay')}
              >
                <div className="payment-method-icon">📱</div>
                <div className="payment-method-name">MB Way</div>
              </div>
              <div 
                className={`payment-method ${paymentMethod === 'PayPal' ? 'selected' : ''}`}
                onClick={() => setPaymentMethod('PayPal')}
              >
                <div className="payment-method-icon">🅿️</div>
                <div className="payment-method-name">PayPal</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button 
                className="action-btn outline"
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}
                onClick={() => setShowPurchaseModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="package-buy-btn"
                style={{ flex: 1 }}
                onClick={purchasePackage}
              >
                Confirmar Compra
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CreditsPage;
